import { getBasePathWithoutTrailingSlash } from "./basePath";

/** API paths that must work without a dashboard session (registration / marketing). */
const PUBLIC_API_PATH_FRAGMENTS = [
  "/auth/register",
  "/auth/registerindividual",
  "/auth/verifycode",
  "/auth/resendcode",
  "/auth/signin",
  "/auth/login",
  "/auth/refresh",
  "/auth/completecompanyregistration",
  "/package/getall",
  "/marketing/",
  "/country",
  "/lookup/getbyparentcode",
  "/lookup/bycode",
  "/lookup/createdraft",
  "/user/getregisterdata",
  "/user/createcompanyuser",
  "/payment/createsession",
  "/payment/processresult",
  "/payment/link/",
];

/**
 * True when `url` contains `fragment` as a full path segment (not a longer path
 * that merely starts with the same characters).
 * Prevents `/package/getall` from matching `/package/getallforbackoffice`.
 */
function urlIncludesPathFragment(url: string, fragment: string): boolean {
  const u = (url || "").toLowerCase();
  const f = (fragment || "").toLowerCase();
  if (!f) return false;

  let from = 0;
  while (from <= u.length) {
    const idx = u.indexOf(f, from);
    if (idx < 0) return false;
    const afterIdx = idx + f.length;
    const after = afterIdx < u.length ? u[afterIdx] : "";
    // Next char must end the path segment (query, end, or another slash for nested paths).
    if (after === "" || after === "?" || after === "/" || after === "&" || after === "#") {
      return true;
    }
    from = idx + 1;
  }
  return false;
}

export function isPublicApiUrl(url: string): boolean {
  return PUBLIC_API_PATH_FRAGMENTS.some((fragment) =>
    urlIncludesPathFragment(url, fragment)
  );
}

/** Pathname relative to router basename (e.g. /UAT/register → /register). */
export function getAppPathname(): string {
  if (typeof window === "undefined") return "";
  const base = getBasePathWithoutTrailingSlash().toLowerCase();
  let path = window.location.pathname.toLowerCase();
  if (base && (path === base || path.startsWith(`${base}/`))) {
    path = path.slice(base.length) || "/";
  }
  if (!path.startsWith("/")) {
    path = `/${path}`;
  }
  return path;
}

export function isRegistrationPage(): boolean {
  const path = getAppPathname();
  return path === "/register" || path.startsWith("/register/");
}

/** True while completing signup — do not wipe an existing dashboard session. */
export function mayClearAuthSession(): boolean {
  if (!isRegistrationPage()) return true;
  return sessionStorage.getItem("__manual_logout__") === "true";
}

/** Do not attach JWT — avoids expired dashboard tokens breaking onboarding. */
export function shouldOmitAuthForRequest(url: string): boolean {
  return isPublicApiUrl(url) || isRegistrationPage();
}

/** Do not clear session / show "token expired" on failed registration API calls. */
export function shouldSkipSessionClearOn401(url: string): boolean {
  return isPublicApiUrl(url) || isRegistrationPage();
}
